import React from 'react';
import {
  Box, VStack, Heading, Text, Button, ButtonText, ScrollView, Spinner,
  HStack, Badge, BadgeText,
} from '@gluestack-ui/themed';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import { useMaterializeContentStudioMaterializePost } from '@/common/api/endpoints/content-studio/content-studio';
import ContentGroup from '@/common/components/ContentGroup';
import { useContentStudioStore } from './store';
import { LoadingOverlay } from './LoadingOverlay';

export const ModuleDraftResult = () => {
  const { moduleDraft, reset } = useContentStudioStore();
  const navigation = useNavigation<NavigationProp<Record<string, unknown>>>();

  const { mutate, isPending } = useMaterializeContentStudioMaterializePost({
    mutation: {
      onSuccess: () => {
        reset();
        navigation.navigate({ name: 'EvaluationDesk', params: {}, merge: false } as never);
      },
    },
  });

  if (!moduleDraft) return null;

  const handleFinish = () => {
    mutate({ data: { moduleDraft } });
  };

  return (
    <Box flex={1} p="$6" maxWidth={1000} alignSelf="center" w="100%">
      <LoadingOverlay isLoading={isPending} title="Saving Module" />
      <VStack space="xl" flex={1}>
        <Box>
          <Heading size="xl">Draft Ready</Heading>
          <Text color="$textSubtle" mt="$2">
            Review your generated module. Clicking save will create the actual module in your library.
          </Text>
        </Box>

        <ContentGroup variant="bordered" flex={1} p="$0">
          <ScrollView flex={1} p="$6">
            <VStack space="xl">
              <Box>
                <Heading size="lg" color="$brandBase">{moduleDraft.title}</Heading>
                <Text mt="$2" size="sm" lineHeight="$md" color="$textBody">
                  {moduleDraft.description}
                </Text>
              </Box>

              <VStack space="lg">
                {moduleDraft.levels.map((level, index) => (
                  <Box
                    key={level.title || index}
                    p="$4"
                    bg="$backgroundLight50"
                    borderRadius="$md"
                    borderWidth={1}
                    borderColor="$borderLight"
                  >
                    <HStack justifyContent="space-between" alignItems="center" mb="$3">
                      <Heading size="sm">
                        Level
                        {' '}
                        {index + 1}
                        :
                        {' '}
                        {level.title}
                      </Heading>
                    </HStack>

                    <VStack space="xs" mb="$4">
                      {level.learningObjectives.map((obj, i) => (
                        <Text key={obj || i} size="xs" color="$textSubtle">
                          •
                          {' '}
                          {obj}
                        </Text>
                      ))}
                    </VStack>

                    {level.tasks && level.tasks.length > 0 && (
                      <VStack space="xs">
                        <Text size="2xs" fontWeight="bold" color="$textHint" textTransform="uppercase">
                          Tasks
                        </Text>
                        <HStack space="sm" flexWrap="wrap">
                          {level.tasks.map((task, i) => (
                            <Badge
                              key={task.title || i}
                              size="sm"
                              variant="outline"
                              action="info"
                              borderRadius="$full"
                              px="$3"
                            >
                              <BadgeText size="2xs" fontWeight="medium">
                                {task.title}
                                {task.type ? ` [${task.type}]` : ''}
                              </BadgeText>
                            </Badge>
                          ))}
                        </HStack>
                      </VStack>
                    )}
                  </Box>
                ))}
              </VStack>
            </VStack>
          </ScrollView>
        </ContentGroup>

        <Button
          onPress={handleFinish}
          isDisabled={isPending}
          size="lg"
          mt="$4"
        >
          {isPending && <Spinner color="$white" mr="$2" />}
          <ButtonText>{isPending ? 'Saving Module...' : 'Save and Create Module'}</ButtonText>
        </Button>
      </VStack>
    </Box>
  );
};
